/**
 * Instagram Story Mockup
 * Simulates how an ad appears in Instagram stories
 */

import React from 'react';
import { X, ChevronUp, MoreHorizontal } from 'lucide-react';

interface InstagramStoryMockupProps {
  children: React.ReactNode;
  adWidth: number;
  adHeight: number;
}

export function InstagramStoryMockup({ children, adWidth, adHeight }: InstagramStoryMockupProps) {
  return (
    <div className="relative bg-black rounded-[2rem] overflow-hidden shadow-2xl w-full max-w-[280px] aspect-[9/16]">
      {/* Phone notch simulation */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-20" />

      {/* Story content */}
      <div className="absolute inset-0">
        {/* Ad image fills the story */}
        <div className="absolute inset-0">
          {children}
        </div>

        {/* Progress bar */}
        <div className="absolute top-8 left-3 right-3 flex gap-1 z-10">
          <div className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
            <div className="h-full w-1/3 bg-white rounded-full" />
          </div>
        </div>

        {/* Header */}
        <div className="absolute top-12 left-3 right-3 flex items-center gap-2 z-10">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center ring-2 ring-white/20">
            <span className="text-white font-bold text-xs">M</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1">
              <span className="font-semibold text-white text-sm drop-shadow-lg">mooncloak</span>
              <span className="text-white/70 text-xs drop-shadow-lg">· Sponsored</span>
            </div>
          </div>
          <button className="p-1">
            <MoreHorizontal className="w-5 h-5 text-white drop-shadow-lg" />
          </button>
          <button className="p-1">
            <X className="w-5 h-5 text-white drop-shadow-lg" />
          </button>
        </div>

        {/* Swipe up CTA */}
        <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center z-10">
          <ChevronUp className="w-6 h-6 text-white animate-bounce drop-shadow-lg" />
          <span className="text-white text-sm font-medium drop-shadow-lg">Learn More</span>
        </div>

        {/* Gradient overlays for text readability */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/50 to-transparent z-[5]" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/50 to-transparent z-[5]" />
      </div>
    </div>
  );
}
